import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { TriangleAlert as AlertTriangle, Trash2, ArrowLeft } from 'lucide-react';
import { AdminService } from '../services/AdminService';

const CONFIRM_TEXT = 'DELETE MY ACCOUNT';
const adminService = new AdminService();

export const DeleteAccount: React.FC = () => {
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const [step, setStep] = useState<'info' | 'confirm'>('info');
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    setDeleting(true);
    try {
      await adminService.deleteOwnAccount();
      await logout();
      navigate('/login', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-white p-4 sm:p-8">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center gap-2 text-white/60 hover:text-white transition mb-8"
        >
          <ArrowLeft size={18} />
          <span>Back to Profile</span>
        </button>

        <div className="bg-slate-800/60 border border-white/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-red-500/20">
              <Trash2 size={24} className="text-red-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Delete Account</h1>
              <p className="text-sm text-white/50">{user?.email}</p>
            </div>
          </div>

          {step === 'info' && (
            <>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-red-200/90 space-y-2">
                    <p className="font-semibold text-red-300">This action is permanent and cannot be undone.</p>
                    <p>Deleting your account will permanently remove:</p>
                    <ul className="list-disc list-inside space-y-1 text-red-200/70">
                      <li>Your profile and personal information</li>
                      <li>All your recordings and performances</li>
                      <li>Tournament entries and scores</li>
                      <li>Coin balance and purchase history</li>
                      <li>Achievements and leaderboard rankings</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/profile')}
                  className="flex-1 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep('confirm')}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 transition font-medium"
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <p className="text-sm text-white/70 mb-4">
                To confirm, type <span className="font-mono font-bold text-white">{CONFIRM_TEXT}</span> below:
              </p>

              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={CONFIRM_TEXT}
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-white/10 focus:border-red-500/50 focus:outline-none text-white placeholder-white/30 font-mono mb-4"
                disabled={deleting}
                autoFocus
              />

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4 text-sm text-red-300">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep('info'); setTyped(''); setError(null); }}
                  className="flex-1 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 transition font-medium"
                  disabled={deleting}
                >
                  Go Back
                </button>
                <button
                  onClick={handleDelete}
                  disabled={typed !== CONFIRM_TEXT || deleting}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 transition font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Permanently'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
